import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { tokens } from '../../theme/tokens.css';

/**
 * Toggle Switch - A boolean on/off control
 *
 * @element toggle-switch
 * @fires change - Fired when toggled, detail contains { checked: boolean }
 */
@customElement('toggle-switch')
export class ToggleSwitch extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: inline-block;
      }

      .switch {
        position: relative;
        width: 44px;
        height: 24px;
        cursor: pointer;
        /* Tapped in the settings rows on mobile — kill the double-tap-zoom
           hesitation and the grey tap flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .track {
        position: absolute;
        inset: 0;
        background-color: var(--color-overlay-medium);
        border-radius: 12px;
        transition: background-color 0.2s ease;
      }

      .switch.checked .track {
        background-color: var(--color-btn-play);
      }

      .thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background-color: var(--color-text-white);
        border-radius: 50%;
        box-shadow: var(--shadow-control);
        transition: transform 0.2s ease;
      }

      .switch.checked .thumb {
        transform: translateX(20px);
      }

      /* Disabled state */
      .switch.disabled {
        cursor: not-allowed;
        opacity: 0.4;
      }

      /* Invisible hit-zone on touch devices — the track is only 24px tall and
         the setting label beside it isn't tappable, so this is the whole
         target. Same trick as range-slider's thumb, but asymmetric on purpose:
         settings rows sit 12px apart at their tightest, so a 10px pad top and
         bottom would overlap the neighbouring row's and a tap in the dead space
         between two toggles would flip whichever won. 6px stops exactly short
         of that. Sideways there is no neighbour — the toggle ends its row — so
         it can take the full 10px. 64x36 rather than 44x24. */
      @media (hover: none) {
        .switch::after {
          content: '';
          position: absolute;
          inset: -6px -10px;
        }
      }

      /* Hover-only — gated so a tap on touch doesn't leave the track stuck in
         the hover colour (this control lives in the settings rows, tapped on
         mobile). Scoped off .disabled rather than restating the base colours
         to undo the hover afterwards. */
      @media (hover: hover) {
        .switch:not(.disabled):hover .track {
          background-color: var(--color-overlay-strong);
        }

        .switch.checked:not(.disabled):hover .track {
          background-color: var(--color-btn-play-hover);
        }
      }
    `,
  ];

  /** Whether the switch is on */
  @property({ type: Boolean })
  checked = false;

  /** Whether the switch is disabled */
  @property({ type: Boolean })
  disabled = false;

  private handleClick = (e: Event) => {
    e.stopPropagation();
    if (this.disabled) return;
    this.checked = !this.checked;
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { checked: this.checked },
        bubbles: true,
        composed: true,
      }),
    );
  };

  override render() {
    const classes = ['switch'];
    if (this.checked) classes.push('checked');
    if (this.disabled) classes.push('disabled');

    return html`
      <div class="${classes.join(' ')}" @click=${this.handleClick}>
        <div class="track"></div>
        <div class="thumb"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'toggle-switch': ToggleSwitch;
  }
}
